"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { wholeWordSource } from "@/lib/tool-kit/text/whole-word";

const ESCAPE_RE = /[.*+?^${}()|[\]\\]/g;

function escapeRegExp(value: string): string {
  return value.replace(ESCAPE_RE, "\\$&");
}

export function replaceAll(
  text: string,
  find: string,
  replaceWith: string,
  caseSensitive: boolean,
  wholeWord: boolean
): { result: string; count: number } {
  const escaped = escapeRegExp(find);
  const pattern = wholeWord ? wholeWordSource(escaped) : escaped;
  const flags = `${caseSensitive ? "g" : "gi"}${wholeWord ? "u" : ""}`;
  const re = new RegExp(pattern, flags);
  let count = 0;
  const result = text.replace(re, () => {
    count += 1;
    return replaceWith;
  });
  return { result, count };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const FIND_AND_REPLACE_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Paste the text you want to edit here...",
  find: "Find",
  findPlaceholder: "Text to find",
  replaceWith: "Replace with",
  replacePlaceholder: "Replacement (leave empty to delete)",
  caseSensitive: "Case-sensitive",
  wholeWordOnly: "Whole word only",
  replaceAll: "Replace All",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  result: "Result",
  replacementsMade: "Replacements made",
  inputCharacters: "Input characters",
  outputCharacters: "Output characters",
} as const;

export default function FindAndReplaceWidget() {
  const [input, setInput] = useState("");
  const [find, setFind] = useState("");
  const [replaceWith, setReplaceWith] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [output, setOutput] = useState("");
  const [replacementCount, setReplacementCount] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(FIND_AND_REPLACE_STRINGS);

  const fmt = useToolNumber();

  function handleReplace() {
    if (input.length === 0 || find.length === 0) {
      return;
    }
    const { result, count } = replaceAll(
      input,
      find,
      replaceWith,
      caseSensitive,
      wholeWord
    );
    setOutput(result);
    setReplacementCount(count);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setReplacementCount(null);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="far-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="far-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Find / Replace fields */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="far-find"
          >
            {strings.find}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="far-find"
            onChange={(e) => setFind(e.target.value)}
            placeholder={strings.findPlaceholder}
            type="text"
            value={find}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="far-replace"
          >
            {strings.replaceWith}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="far-replace"
            onChange={(e) => setReplaceWith(e.target.value)}
            placeholder={strings.replacePlaceholder}
            type="text"
            value={replaceWith}
          />
        </div>
      </div>

      {/* Options */}
      <div className="flex flex-wrap gap-3">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={caseSensitive}
            className="accent-primary"
            onChange={(e) => setCaseSensitive(e.target.checked)}
            type="checkbox"
          />
          {strings.caseSensitive}
        </label>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={wholeWord}
            className="accent-primary"
            onChange={(e) => setWholeWord(e.target.checked)}
            type="checkbox"
          />
          {strings.wholeWordOnly}
        </label>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0 || find.length === 0}
          onClick={handleReplace}
          type="button"
        >
          {strings.replaceAll}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {replacementCount !== null && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="far-output"
          >
            {strings.result}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="far-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {replacementCount !== null && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.replacementsMade}
            value={fmt(replacementCount)}
          />
          <StatCard label={strings.inputCharacters} value={fmt(input.length)} />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(output.length)}
          />
        </div>
      )}
    </>
  );
}
